'use client';

import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Phone, MoreVertical, Search, Video, User, BellOff, Trash2, Ban, Settings, Images } from 'lucide-react';

interface ChatHeaderProps {
  isDark?: boolean;
  onToggleTheme?: () => void;
  isTyping: boolean;
  onBack?: () => void;
  onViewProfile?: () => void;
  onOpenSettings?: () => void;
  onVoiceCall?: () => void;
  onVideoCall?: () => void;
  onOpenGallery?: () => void;
  remoteUserName?: string;
  isBlocked?: boolean;
  onBlockContact?: () => void;
  onDeleteChat?: () => void;
}

export default function ChatHeader({ isTyping, onBack, onViewProfile, onOpenSettings, onVoiceCall, onVideoCall, onOpenGallery, remoteUserName = 'Ate Boni', isBlocked = false, onBlockContact, onDeleteChat }: ChatHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <header
      className="chat-header-surface flex items-center gap-2.5 px-2 sm:px-3 flex-shrink-0 z-20 relative"
      style={{ height: '64px', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      role="banner">

      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center justify-center w-[44px] h-[44px] rounded-full hover:bg-white/8 transition-colors duration-150 active:scale-95 flex-shrink-0"
        aria-label="Go back"
        title="Back">

        <ArrowLeft size={22} className="text-white" />
      </button>

      {/* Avatar — tappable to open profile */}
      <button
        onClick={onViewProfile}
        className="relative flex-shrink-0 focus:outline-none"
        aria-label="View profile">

        <div
          className="w-[48px] h-[48px] rounded-full flex items-center justify-center text-white font-bold text-[17px] flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)' }}>

          AB
        </div>
        {/* Online indicator */}
        <span
          className="absolute bottom-[1px] right-[1px] w-[12px] h-[12px] bg-green-400 rounded-full border-2"
          style={{ borderColor: 'var(--chat-header)' }}
          aria-label="Online" />

      </button>

      {/* Identity — tappable to open profile */}
      <button
        onClick={onViewProfile}
        className="flex-1 min-w-0 ml-0 text-left focus:outline-none flex flex-col justify-center"
        aria-label="View profile">

        <p className="text-[17px] font-semibold text-white leading-tight truncate tracking-[0.01em] mt-1">
          {remoteUserName}
        </p>
        <p className="text-white/55 mt-1.5 text-[8px] whitespace-nowrap">
          last seen today at 10:42 AM
        </p>
      </button>

      {/* Right actions */}
      <div className="flex items-center gap-0 flex-shrink-0">
        <button
          onClick={onVoiceCall}
          title="Voice call"
          aria-label="Voice call"
          className="flex items-center justify-center w-[36px] h-[36px] rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-all duration-150 active:scale-90 focus:outline-none focus:ring-2 focus:ring-white/20">

          <Phone size={20} />
        </button>
        <button
          onClick={onVideoCall}
          title="Video call"
          aria-label="Video call"
          className="flex items-center justify-center w-[36px] h-[36px] rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-all duration-150 active:scale-90 focus:outline-none focus:ring-2 focus:ring-white/20">

          <Video size={20} />
        </button>
        <HeaderIconButton icon={<Search size={20} />} label="Search messages" className="hidden sm:flex" />

        {/* Gallery button */}
        <button
          onClick={onOpenGallery}
          title="Media gallery"
          aria-label="Media gallery"
          className="flex items-center justify-center w-[36px] h-[36px] rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-all duration-150 active:scale-90 focus:outline-none focus:ring-2 focus:ring-white/20">

          <Images size={20} />
        </button>

        {/* More options with dropdown */}
        <div className="relative" ref={menuRef}>
          <HeaderIconButton
            icon={<MoreVertical size={20} />}
            label="More options"
            onClick={() => setMenuOpen((v) => !v)} />


          {menuOpen &&
          <div
            className="absolute right-0 top-[calc(100%+4px)] w-[200px] rounded-2xl overflow-hidden animate-scale-in z-50"
            style={{
              background: 'var(--context-bg)',
              border: '1px solid var(--context-border)',
              boxShadow: 'var(--context-shadow)',
              transformOrigin: 'top right'
            }}>

              <MenuItem
              icon={<User size={16} />}
              label="View Profile"
              onClick={() => {setMenuOpen(false);onViewProfile?.();}} />

              <MenuItem
              icon={<Images size={16} />}
              label="Media Gallery"
              onClick={() => {setMenuOpen(false);onOpenGallery?.();}} />

              <MenuItem
              icon={<Settings size={16} />}
              label="Settings"
              onClick={() => {setMenuOpen(false);onOpenSettings?.();}} />

              <MenuDivider />
              <MenuItem
              icon={<BellOff size={16} />}
              label="Mute Notifications"
              onClick={() => setMenuOpen(false)} />

              <MenuItem
              icon={<Search size={16} />}
              label="Search Messages"
              onClick={() => setMenuOpen(false)} />

              <MenuDivider />
              <MenuItem
              icon={<Ban size={16} />}
              label={isBlocked ? 'Unblock Contact' : 'Block Contact'}
              onClick={() => {setMenuOpen(false);onBlockContact?.();}}
              danger />

              <MenuItem
              icon={<Trash2 size={16} />}
              label="Delete Chat"
              onClick={() => {setMenuOpen(false);onDeleteChat?.();}}
              danger />

            </div>
          }
        </div>
      </div>
    </header>);

}

function HeaderIconButton({
  icon,
  label,
  onClick,
  className = ''





}: {icon: React.ReactNode;label: string;onClick?: () => void;className?: string;}) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`flex items-center justify-center w-[44px] h-[44px] rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-all duration-150 active:scale-90 focus:outline-none focus:ring-2 focus:ring-white/20 ${className}`}>

      {icon}
    </button>);

}

function MenuItem({
  icon, label, onClick, danger = false





}: {icon: React.ReactNode;label: string;onClick: () => void;danger?: boolean;}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-150 ${
      danger ? 'hover:bg-red-500/10 text-red-400' : 'hover:bg-white/6 text-white'}`
      }>

      <span style={{ color: danger ? undefined : 'var(--primary)', opacity: danger ? 1 : 0.85 }}>
        {icon}
      </span>
      <span className="text-[14px] font-medium">{label}</span>
    </button>);

}

function MenuDivider() {
  return <div className="mx-3" style={{ height: '1px', background: 'var(--border)' }} />;
}